import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js';
import { isPostHogEnabled } from './isPostHogEnabled';
import { maskInputElement, maskTextElement } from '../analytics/dataMasking';

type PostHogClient = Pick<PostHog, 'init' | 'register'>;

export function initializePostHog(
  posthog: PostHogClient,
  {
    environment,
    release,
  }: {
    environment: string;
    release: string;
  },
) {
  if (!isPostHogEnabled()) {
    return false;
  }

  // Intentional PostHog enablement may still need a deliberate CSP decision for
  // us-assets.i.posthog.com or a different PostHog config.
  const projectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;

  if (!projectToken || !apiHost) {
    console.warn('PostHog is enabled but VITE_POSTHOG_PROJECT_TOKEN or VITE_POSTHOG_HOST is not configured.');
    return false;
  }

  const config = {
    api_host: apiHost,
    defaults: '2026-01-30',
    secure_cookie: true,
    logs: {
      serviceName: 'frontend',
      environment,
      serviceVersion: release,
    },
    autocapture: {
      capture_copied_text: false,
      css_selector_ignorelist: ['.ph-no-autocapture', '[data-ph-no-autocapture]', '[data-pii-role]'],
      element_attribute_ignorelist: ['aria-label'],
    },
    capture_dead_clicks: {
      css_selector_ignorelist: [
        '.ph-no-deadclick',
        '.ph-no-autocapture',
        '[data-ph-no-autocapture]',
        '[data-pii-role]',
      ],
      element_attribute_ignorelist: ['aria-label'],
    },
    before_send: (event: CaptureResult | null): CaptureResult | null => event,
    session_recording: {
      maskAllInputs: true,
      maskInputFn: maskInputElement,
      maskTextSelector: '*',
      maskTextFn: maskTextElement,
    },
  } satisfies Partial<PostHogConfig>;

  posthog.init(projectToken, config);

  posthog.register({
    environment,
    release,
  });

  return true;
}

export function disableEventSending(posthog: PostHog) {
  posthog.set_config({
    before_send: (_: CaptureResult | null): CaptureResult | null => null,
  });
}

export function enableEventSending(posthog: PostHog) {
  posthog.set_config({
    before_send: (event: CaptureResult | null): CaptureResult | null => event,
  });
}
